import React, { useState, useRef, useEffect, useCallback } from 'react';
import api from '../api';
import './ChatWidget.css';

const FALLBACK_WELCOME =
  "Hello — I'm Ace Kaatha Assistant. Ask about purchases, aggregation, discounts, journals, tax, or period close.";

async function revealWords(fullText, onUpdate) {
  const parts = String(fullText || '').split(/(\s+)/).filter((p) => p.length);
  let acc = '';
  for (let i = 0; i < parts.length; i += 1) {
    acc += parts[i];
    onUpdate(acc);
    // Slightly faster for whitespace, slower for words — feels like typing
    const delay = /^\s+$/.test(parts[i]) ? 12 : 28 + Math.min(40, parts[i].length);
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, delay));
  }
  onUpdate(acc);
}

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState('');
  const [welcome, setWelcome] = useState('');
  const bottomRef = useRef(null);
  const revealCancel = useRef(false);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open, sending, streaming]);

  useEffect(() => () => {
    revealCancel.current = true;
  }, []);

  const loadWelcome = useCallback(async () => {
    try {
      const res = await api.get('/ai/chat/config/');
      const text = (res.data?.welcome_message || '').trim();
      setWelcome(text || FALLBACK_WELCOME);
    } catch {
      setWelcome(FALLBACK_WELCOME);
    }
  }, []);

  const ensureSession = async () => {
    if (sessionId) return sessionId;
    const res = await api.post('/ai/chat/');
    const id = res.data?.id;
    if (!id) {
      throw new Error('Chat session did not return an id');
    }
    setSessionId(id);
    return id;
  };

  const handleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      setError('');
      loadWelcome();
      if (!sessionId) {
        try {
          await ensureSession();
        } catch (err) {
          setError(err.response?.data?.detail || err.message || 'Could not start chat session');
        }
      }
    }
  };

  const appendStreamingAssistant = async (fullText) => {
    revealCancel.current = false;
    setSending(false);
    setStreaming(true);
    setMessages((prev) => [...prev, { role: 'assistant', content: '', streaming: true }]);

    await revealWords(fullText, (partial) => {
      if (revealCancel.current) return;
      setMessages((prev) => {
        const copy = [...prev];
        const last = copy[copy.length - 1];
        if (last?.role === 'assistant' && last.streaming) {
          copy[copy.length - 1] = { ...last, content: partial };
        }
        return copy;
      });
    });

    if (!revealCancel.current) {
      setMessages((prev) => {
        const copy = [...prev];
        const last = copy[copy.length - 1];
        if (last?.role === 'assistant') {
          copy[copy.length - 1] = { role: 'assistant', content: fullText };
        }
        return copy;
      });
    }
    setStreaming(false);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending || streaming) return;

    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setSending(true);
    setError('');

    try {
      const id = await ensureSession();
      const res = await api.post(`/ai/chat/${id}/message/`, { message: text });
      const reply = res.data?.message || 'No response';
      await appendStreamingAssistant(reply);
    } catch (err) {
      const detail =
        err.response?.data?.error ||
        err.response?.data?.detail ||
        err.message ||
        'Failed to send message';
      setError(detail);
      setSending(false);
      await appendStreamingAssistant(
        'Sorry — chat request failed. If this keeps happening, restart the Ace backend container.'
      );
    }
  };

  const busy = sending || streaming;
  const showWelcome = messages.length === 0 && !sending;

  return (
    <div className="chat-widget">
      {open && (
        <div className="chat-panel">
          <div className="chat-header">
            <div>Ace Kaatha Assistant</div>
            <button type="button" className="chat-close" onClick={() => setOpen(false)}>
              ×
            </button>
          </div>
          <div className="chat-messages">
            {showWelcome && (
              <div className="chat-bubble assistant chat-welcome">
                {welcome || FALLBACK_WELCOME}
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={`chat-bubble ${m.role}${m.streaming ? ' chat-streaming' : ''}`}
              >
                {m.content}
                {m.streaming && <span className="chat-caret" aria-hidden="true" />}
              </div>
            ))}
            {sending && (
              <div className="chat-bubble assistant chat-thinking" aria-live="polite">
                <span className="chat-thinking-label">Agent thinking</span>
                <span className="chat-thinking-dots" aria-hidden="true">
                  <i />
                  <i />
                  <i />
                </span>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          {error && <div className="chat-error">{error}</div>}
          <form className="chat-input-row" onSubmit={sendMessage}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type a message..."
              disabled={busy}
            />
            <button type="submit" disabled={busy || !input.trim()}>
              {sending ? '...' : 'Send'}
            </button>
          </form>
        </div>
      )}
      <button type="button" className="chat-fab" onClick={handleOpen}>
        {open ? '×' : 'Chat'}
      </button>
    </div>
  );
};

export default ChatWidget;
